import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { injectIntl, FormattedMessage, FormattedDate } from 'react-intl';

// Initial component setup for airbnb/react-dates library
import { DayPickerSingleDateController } from 'react-dates';
import { SingleDatePickerPhrases } from 'react-dates/lib/defaultPhrases';
import getPhrasePropTypes from 'react-dates/lib/utils/getPhrasePropTypes';

import { childrenShape } from '@bibliocommons/bc-prop-types';
import { Angle } from '@bibliocommons/deprecated-base-icons';
import FullScreenOverlay from '@bibliocommons/deprecated-base-full-screen-overlay';
import { TextButton, SecondaryTextButton, SecondaryGhostButton } from '@bibliocommons/base-buttons';
import { CalendarEmpty } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

// Initial css setup for airbnb/react-dates library
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './react_dates_overrides.scss';

import NativeDatePicker from './NativeDatePicker';
import './AccessibleDatePicker.scss';

function formatDate(date, format = 'YYYY-MM-DD') {
  if (!date) return '';
  const dateObj = moment.isMoment(date) ? date : moment(date);
  return dateObj.format(format);
}

function isMobile() {
  return typeof window !== 'undefined' && /Android|iPhone|iPad/i.test(window.navigator.userAgent);
}

export function AccessibleDatePicker(props) {
  const {
    initialDate,
    open,
    id,
    handleClose,
    handleOpen,
    dataKey,
    intl,
    children,
    fullScreen,
    className,
    minDate,
    maxDate,
    highlightSelectedDateRange: highlightRange,
    buttonTag: ButtonTag = SecondaryGhostButton,
    buttonTagClassName,
    disabled,
    showLoadingText,
    isLoading,
    onDateChange,
    ariaDescribedBy
  } = props;

  const [date, setDate] = useState(initialDate ? moment(initialDate) : null);
  const datePickerRef = useRef(null);

  // Sync state with initialDate prop changes
  useEffect(() => {
    if (formatDate(initialDate) !== formatDate(date)) {
      setDate(initialDate ? moment(initialDate) : null);
    }
  }, [initialDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle clicks outside the date picker to close it
  useEffect(() => {
    function handleDocumentClick(e) {
      const wrapper = datePickerRef.current;
      if (wrapper && wrapper !== e.target && !wrapper.contains(e.target)) {
        handleClose();
      }
    }

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [handleClose]);

  const handleDateChange = useCallback(
    newDate => {
      onDateChange(formatDate(newDate));
      setDate(newDate ? moment(newDate) : null);
      setTimeout(() => {
        handleClose();
      }, 100);
    },
    [onDateChange, handleClose]
  );

  const isOutsideRange = useCallback(
    day => {
      const isBeforeMinDate = minDate && day.isBefore(moment(minDate), 'day');
      const isAfterMaxDate = maxDate && day.isAfter(moment(maxDate), 'day');

      if (minDate && maxDate) return isBeforeMinDate || isAfterMaxDate;
      if (minDate) return isBeforeMinDate;
      if (maxDate) return isAfterMaxDate;
      return false;
    },
    [minDate, maxDate]
  );

  const isDayHighlighted = useCallback(
    day => highlightRange && moment(day).isAfter(minDate) && moment(day).isBefore(date),
    [highlightRange, minDate, date]
  );

  const phraseOverrides = {
    ...SingleDatePickerPhrases,
    jumpToPrevMonth: intl.formatMessage({ id: 'calendar_prev_month' }),
    jumpToNextMonth: intl.formatMessage({ id: 'calendar_next_month' }),
    dateIsUnavailable: ({ date: d }) => intl.formatMessage({ id: 'calendar_date_is_unavailable' }, { date: d }),
    dateIsSelected: ({ date: d }) => intl.formatMessage({ id: 'calendar_date_is_selected' }, { date: d })
  };

  function renderTodayButton() {
    return (
      <SecondaryTextButton
        className="item-column"
        dataKey="today-button"
        handleClick={() => handleDateChange(moment().format())}
      >
        <FormattedMessage
          id="calendar_today"
          values={{
            date: (
              <ScreenReaderMessage>
                <FormattedDate value={moment().format()} year="numeric" month="short" day="2-digit" />
              </ScreenReaderMessage>
            )
          }}
        />
      </SecondaryTextButton>
    );
  }

  function renderCloseButton() {
    return (
      <TextButton className="item-column" dataKey="close-button" handleClick={handleClose}>
        <FormattedMessage id="button_close" />
      </TextButton>
    );
  }

  function renderResetButton() {
    if (!date) return null;
    return (
      <SecondaryTextButton
        className="item-column"
        data-test-id="reset-button"
        dataKey="reset-button"
        handleClick={() => handleDateChange(null)}
      >
        <FormattedMessage id="reset" />
      </SecondaryTextButton>
    );
  }

  function renderExtraButtons() {
    return (
      <div className="date-picker-extra-buttons item-row">
        {renderTodayButton()}
        {renderResetButton()}
        {renderCloseButton()}
      </div>
    );
  }

  function renderChildren() {
    return (
      <ButtonTag
        dataKey={dataKey}
        handleClick={handleOpen}
        className={buttonTagClassName}
        disabled={disabled}
        showLoadingText={showLoadingText}
        isLoading={isLoading}
        aria-hidden={isMobile()}
        tabIndex={isMobile() ? '-1' : undefined}
        aria-describedby={ariaDescribedBy}
      >
        <CalendarEmpty />
        <span>{children}</span>
      </ButtonTag>
    );
  }

  function renderDatePickerPopUp(pickerProps) {
    return (
      <div className="cp-accessible-date-picker-pop-up" ref={datePickerRef}>
        <div className="cp-accessible-date-picker-content">{renderChildren()}</div>
        {open && (
          <DayPickerSingleDateController
            {...pickerProps}
            isOutsideRange={isOutsideRange}
            date={date}
            withPortal={false}
            phrases={phraseOverrides}
            onDateChange={handleDateChange}
            renderCalendarInfo={renderExtraButtons}
            weekDayFormat="ddd"
            isDayHighlighted={isDayHighlighted}
          />
        )}
      </div>
    );
  }

  function renderFullScreenOverlayDatePicker(pickerProps) {
    return (
      <div>
        <FullScreenOverlay
          className="accessible-date-picker-full-screen-overlay"
          open={open}
          handleClose={handleClose}
          dataKey="accessible-date-picker-full-screen-overlay"
        >
          <DayPickerSingleDateController
            {...pickerProps}
            isOutsideRange={isOutsideRange}
            date={date}
            phrases={phraseOverrides}
            onDateChange={handleDateChange}
            renderCalendarInfo={renderExtraButtons}
            weekDayFormat="ddd"
          />
        </FullScreenOverlay>
        <div className="cp-accessible-date-picker-content">{renderChildren()}</div>
      </div>
    );
  }

  function renderNativeDatePicker() {
    return (
      <NativeDatePicker
        className="mobile-datepicker"
        id={`${id}-native-picker`}
        value={formatDate(date)}
        onChange={handleDateChange}
        minDate={formatDate(minDate)}
        maxDate={formatDate(maxDate)}
      >
        {renderChildren()}
      </NativeDatePicker>
    );
  }

  function renderContent(pickerProps) {
    if (isMobile()) return renderNativeDatePicker();
    return fullScreen ? renderFullScreenOverlayDatePicker(pickerProps) : renderDatePickerPopUp(pickerProps);
  }

  const pickerProps = _.omit(props, [
    'initialDate',
    'open',
    'id',
    'handleClose',
    'handleOpen',
    'dataKey',
    'intl',
    'children',
    'displayFormat',
    'inputIconPosition',
    'fullScreen',
    'className',
    'minDate',
    'maxDate',
    'highlightSelectedDateRange'
  ]);

  return <div className={`cp-accessible-date-picker ${className}`}>{renderContent(pickerProps)}</div>;
}

const defaultProps = {
  numberOfMonths: 1,
  displayFormat: () => moment.localeData().longDateFormat('L'),
  inputIconPosition: 'after',
  navPrev: <Angle.Left />,
  navNext: <Angle.Right />,
  hideKeyboardShortcutsPanel: true,
  withPortal: true,
  initialDate: null,
  focused: true,
  fullScreen: false,
  className: '',
  minDate: moment().format('YYYY-MM-DD')
};

const propTypes = {
  open: PropTypes.bool,
  id: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  children: childrenShape,
  fullScreen: PropTypes.bool,
  buttonTag: PropTypes.elementType,
  buttonTagClassName: PropTypes.string,
  className: PropTypes.string,
  dataKey: PropTypes.string,
  handleOpen: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showLoadingText: PropTypes.bool,
  isLoading: PropTypes.bool,
  initialDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onDateChange: PropTypes.func.isRequired,
  focused: PropTypes.bool,
  onFocusChange: PropTypes.func,
  navPrev: PropTypes.node,
  navNext: PropTypes.node,
  phrases: PropTypes.shape(getPhrasePropTypes(SingleDatePickerPhrases)),
  noBorder: PropTypes.bool,
  hideKeyboardShortcutsPanel: PropTypes.bool,
  displayFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  small: PropTypes.bool,
  withPortal: PropTypes.bool,
  inputIconPosition: PropTypes.string,
  numberOfMonths: PropTypes.number,
  renderCalendarInfo: PropTypes.func,
  customInputIcon: PropTypes.node,
  focus: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  highlightSelectedDateRange: PropTypes.bool,
  ariaDescribedBy: PropTypes.string
};

AccessibleDatePicker.propTypes = propTypes;
AccessibleDatePicker.defaultProps = defaultProps;

export default injectIntl(AccessibleDatePicker);
